import * as React from "react"

import { Symbols } from "@tenforce/toolbox-fontmap"
import { Action } from "@tenforce/toolbox-button-push-react"
import { Cell, RowContent } from "@tenforce/toolbox-table-react"

import bem from "../../../../bem"
import { formatLabel } from "../../../../util/Strings"

import FileDiff from "../../../diff/FileDiff"
import { FieldItems, ReadOnly } from "@tenforce/toolbox-field-react"
import { Labels } from "../../../../util/Translations"

const b = bem.with("NewUI__packageImportRow")

declare let window: OldUIContext.TenForceWindow

interface Props {
    packageDiff: Deployment.PackageDiff
    makeSnapshot: (id: string) => void
}

interface State {
    opened: boolean
}

export const PlaceholderRow = (): JSX.Element => (
    <RowContent className="is-placeholder is-loading"></RowContent>
)

export class Row extends React.PureComponent<Props, State> {
    constructor(props: Props) {
        super(props)
        this.state = { opened: false }
    }

    render(): JSX.Element {
        const { packageDiff } = this.props
        const {
            addinDiffs,
            addinDiffsLoading,
            baselineTime,
            changes,
            configuration,
            snapshotTime,
        } = packageDiff

        const totalChanges =
            changes.length +
            addinDiffs.map((addin) => addin.diff.changes.length).sum()

        const areThereChanges = totalChanges > 0

        const parsedBaselineTime = !!baselineTime
            ? new Date(baselineTime).toLocaleString("en-GB")
            : "-"

        const parsedSnapshotTime = !!snapshotTime
            ? new Date(snapshotTime).toLocaleString("en-GB")
            : "-"

        const { NoChangedFiles, SeeNChangedFiles, TakeSnapshot } = Labels
        return (
            <>
                <RowContent
                    extraClasses={addinDiffsLoading ? "is-loading" : ""}
                >
                    <Cell>
                        <Action
                            data-ft-deployment="configuration-see-changes"
                            extraClasses="tw-pl-6"
                            isDisabled={!areThereChanges}
                            isReadOnly={!areThereChanges}
                            onClick={this.toggleOpen}
                        >
                            {areThereChanges ? (
                                <>
                                    {this.state.opened
                                        ? Symbols.ExpandedBelow
                                        : Symbols.Expand}{" "}
                                    {formatLabel(
                                        SeeNChangedFiles,
                                        totalChanges,
                                    )}
                                </>
                            ) : (
                                NoChangedFiles
                            )}
                        </Action>
                    </Cell>

                    <Cell>
                        <FieldItems>
                            <ReadOnly
                                value={configuration.name}
                                title={configuration.name}
                            />
                        </FieldItems>
                    </Cell>

                    <Cell>
                        <FieldItems>
                            <ReadOnly
                                value={configuration.version}
                                title={configuration.version}
                            />
                        </FieldItems>
                    </Cell>

                    <Cell>
                        <FieldItems>
                            <ReadOnly
                                value={parsedBaselineTime}
                                title={parsedBaselineTime}
                            />
                        </FieldItems>
                    </Cell>

                    <Cell>
                        <FieldItems>
                            <ReadOnly
                                value={parsedSnapshotTime}
                                title={parsedSnapshotTime}
                            />
                        </FieldItems>
                    </Cell>

                    <Cell>
                        <Action
                            data-ft-deployment="configuration-snapshot"
                            extraClasses="tw-pl-6"
                            onClick={this.snapshot}
                            title={TakeSnapshot}
                        >
                            {Symbols.Shortcut}
                        </Action>
                    </Cell>
                </RowContent>

                {this.state.opened && totalChanges > 0 && (
                    <ul className="tw-m-3 tw-border-2 tw-border-solid tw-border-yellow-700">
                        {changes.length > 0 && (
                            <div className={b("config")}>
                                {changes.map((change, i) => (
                                    <li key={`${configuration.id}@${i}`}>
                                        <FileDiff {...change} />
                                    </li>
                                ))}
                            </div>
                        )}

                        {(addinDiffs ?? []).length > 0 && (
                            <div className={b("addins")}>
                                {addinDiffs?.sort(this.compareAddinName).map(
                                    (addin, i) =>
                                        addin.diff.changes.length > 0 && (
                                            <div
                                                className="tw-m-3
                                                tw-border-2 tw-border-solid tw-border-green-500 tw-w-auto tw-bg-slate-100"
                                                key={`addin${i}`}
                                            >
                                                <div className="tw-bg-green-500 tw-font-mono tw-font-semibold tw-text-base tw-px-4 tw-py-2 tw-text-left">
                                                    {addin.addInName}{" "}
                                                    {addin.addInVersion}
                                                </div>
                                                <ul
                                                    className={b(
                                                        "addinFileList",
                                                    )}
                                                >
                                                    {addin.diff.changes.map(
                                                        (change, j) => (
                                                            <li
                                                                key={`addinDiff${i}@${j}`}
                                                            >
                                                                <FileDiff
                                                                    {...change}
                                                                />
                                                            </li>
                                                        ),
                                                    )}
                                                </ul>
                                            </div>
                                        ),
                                )}
                            </div>
                        )}
                    </ul>
                )}
            </>
        )
    }

    compareAddinName(a: Addins.Addin, b: Addins.Addin): number {
        return a.addInName
            .toLocaleUpperCase()
            .localeCompare(b.addInName.toLocaleUpperCase())
    }

    toggleOpen = (): void => {
        this.setState({
            opened: !this.state.opened,
        })
    }

    snapshot = (): void => {
        const { packageDiff, makeSnapshot } = this.props
        window.feedback.clear()
        window.feedback.displayInfo("Committing changes...", false, false)
        makeSnapshot(packageDiff.configuration.id)
    }
}
